import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { PermitCategory } from '../../models/permit-category.model';
import { PermitStatus } from '../../models/permit-status.model';
import { PermitDepartment } from '../../models/permit-department.model';
import { PermitSettingsService } from '../../services/permit-settings.service';
import { PermitV2Service } from '../../services/permits.abstract.service';
import { SyncOutcome, SyncStep, SyncStepKey, SyncStepState } from '../../models/permit-sync.model';
import { BcitiConfirmDialogComponent } from '../../../global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import { PermitSyncProgressDialogComponent } from '../dialogs/permit-sync-progress-dialog/permit-sync-progress-dialog.component';
import { PermitConfigStatusesComponent } from '../permit-config-statuses/permit-config-statuses.component';
import { PermitConfigCategoriesComponent } from '../permit-config-categories/permit-config-categories.component';
import { PermitConfigDepartmentsComponent } from '../permit-config-departments/permit-config-departments.component';
import { PermitTypeEditorComponent } from '../permit-type-editor/permit-type-editor.component';

export type PermitConfigSection = 'categories' | 'statuses' | 'departments' | 'tags';

@Component({
    selector: 'app-permit-config',
    templateUrl: './permit-config.component.html',
    styleUrls: ['./permit-config.component.scss'],
})
export class PermitConfigComponent implements OnInit, OnDestroy {
    @ViewChild(PermitConfigStatusesComponent) statusesSection: PermitConfigStatusesComponent;
    @ViewChild(PermitConfigCategoriesComponent) categoriesSection: PermitConfigCategoriesComponent;
    @ViewChild(PermitConfigDepartmentsComponent) departmentsSection: PermitConfigDepartmentsComponent;
    @ViewChild(PermitTypeEditorComponent) typeEditorComponent: PermitTypeEditorComponent;

    readonly sections: PermitConfigSection[] = ['categories', 'statuses', 'departments', 'tags'];
    selectedSection: PermitConfigSection = 'categories';

    isCreatingCategory = false;
    isEditingCategory = false;
    selectedCategory: PermitCategory | null = null;

    isCreatingStatus = false;
    isEditingStatus = false;
    selectedStatus: PermitStatus | null = null;

    isCreatingDepartment = false;
    isEditingDepartment = false;
    selectedDepartment: PermitDepartment | null = null;
    departmentNextOrder = 0;

    isEditingType = false;
    typeEditorCategoryId: string | null = null;
    typeEditorTypeId: string | null = null;

    // Statuses are owned by the external system when the org is connected: no create path.
    statusesLocked = false;

    /** Whether a Permits-type connector exists — gates the manual sync action. */
    hasPermitConnector = false;

    private destroy$ = new Subject<void>();

    constructor(
        private settingsService: PermitSettingsService,
        private permitService: PermitV2Service,
        private dialog: MatDialog,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.settingsService.isExternallyManaged$.pipe(takeUntil(this.destroy$)).subscribe((connected) => {
            this.statusesLocked = connected;
        });
        this.settingsService.hasPermitConnector$.pipe(takeUntil(this.destroy$)).subscribe((hasConnector) => {
            this.hasPermitConnector = hasConnector;
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get isFormMode(): boolean {
        return this.isCreatingCategory || this.isEditingCategory || this.isCreatingStatus || this.isEditingStatus || this.isCreatingDepartment || this.isEditingDepartment || this.isEditingType;
    }

    get headerTitle(): string {
        if (this.isCreatingCategory || this.isEditingCategory) {
            return this.isEditingCategory ? 'permit.config.categories.form_title_edit' : 'permit.config.categories.form_title_new';
        }
        if (this.isCreatingStatus || this.isEditingStatus) {
            return this.isEditingStatus ? 'permit.config.statuses.form_title_edit' : 'permit.config.statuses.form_title_new';
        }
        if (this.isCreatingDepartment || this.isEditingDepartment) {
            return this.isEditingDepartment ? 'permit.config.departments.form_title_edit' : 'permit.config.departments.form_title_new';
        }
        if (this.isEditingType) {
            return this.typeEditorTypeId ? 'permit.config.editor.title_edit' : 'permit.config.editor.title_create';
        }
        return `permit.config.${this.selectedSection}.title`;
    }

    get showCreateButton(): boolean {
        if (this.isFormMode) return false;
        if (this.selectedSection === 'statuses') return !this.statusesLocked;
        return this.selectedSection === 'categories' || this.selectedSection === 'departments';
    }

    /**
     * Only on the categories section, and only when a connector actually exists —
     * without one the backend rejects the call, so offering it would be a dead end.
     */
    get showSyncButton(): boolean {
        return !this.isFormMode && this.selectedSection === 'categories' && this.hasPermitConnector;
    }

    readonly syncButtonTranslationKey = 'permit.config.categories.sync';

    onHeaderSync(): void {
        const confirmation = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: this.translate.instant('permit.config.categories.sync_confirm_title'),
                    body: this.translate.instant('permit.config.categories.sync_confirm_body'),
                    confirmLabel: this.translate.instant('permit.config.categories.sync'),
                    cancelLabel: this.translate.instant('permit.config.common.cancel'),
                },
                component: { data: {}, entry: null },
            },
        });

        confirmation
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (!confirmed) return;
                void this.runConnectorSync();
            });
    }

    /**
     * Walks the four steps in sequence, pushing state into the progress dialog.
     *
     * Sequential on purpose: a single call running both syncs in parallel would
     * light up both checkmarks at the same instant, which is the whole point of
     * showing them. A failed categories step does NOT stop the permits step — the
     * two are independent — but a failed first step does, since there is then no
     * connector to talk to.
     *
     * Returns the promise so callers can await the whole sequence; onHeaderSync
     * fires it and forgets, but tests need to await it (fixture.whenStable does
     * not flush this chain).
     */
    async runConnectorSync(): Promise<void> {
        const steps$ = new BehaviorSubject<SyncStep[]>([
            { key: 'check', state: 'pending' },
            { key: 'categories', state: 'pending' },
            { key: 'permits', state: 'pending' },
            { key: 'refresh', state: 'pending' },
        ]);
        const progress$ = new BehaviorSubject<number>(0);
        const done$ = new BehaviorSubject<boolean>(false);

        const dialogRef = this.dialog.open(PermitSyncProgressDialogComponent, {
            width: '440px',
            disableClose: true,
            data: { steps$, progress$, done$ },
        });

        const setState = (key: SyncStepKey, state: SyncStepState, reason?: SyncStep['reason']) => steps$.next(steps$.value.map((step) => (step.key === key ? { ...step, state, reason } : step)));

        // Creeps toward the bound while waiting so the bar never looks frozen, but
        // stops short of it: only a completed step may reach its bound.
        let creep: ReturnType<typeof setInterval> | null = null;
        const advanceTowards = (bound: number) => {
            if (creep) clearInterval(creep);
            creep = setInterval(() => progress$.next(progress$.value + (bound - progress$.value) * 0.15), 400);
        };
        const settleAt = (bound: number) => {
            if (creep) clearInterval(creep);
            creep = null;
            progress$.next(bound);
        };

        const outcomeToState = (outcome: SyncOutcome): SyncStepState => (outcome === 'ok' ? 'ok' : outcome === 'running' ? 'running' : 'failed');

        try {
            setState('check', 'active');
            advanceTowards(10);
            // Rarely fails in practice: the button is gated on this same signal, so
            // reaching here usually means it was true. It stays as the guard for the
            // narrow case where the organisation changed after the page rendered.
            const hasConnector = await this.settingsService.hasPermitConnector$.pipe(take(1)).toPromise();
            setState('check', hasConnector ? 'ok' : 'failed', hasConnector ? undefined : 'not-configured');
            settleAt(10);
            if (!hasConnector) return;

            for (const [key, bound, call] of [
                ['categories', 55, () => this.permitService.syncConnectorCategories()],
                ['permits', 90, () => this.permitService.syncConnectorPermits()],
            ] as [SyncStepKey, number, () => ReturnType<typeof this.permitService.syncConnectorPermits>][]) {
                setState(key, 'active');
                advanceTowards(bound);
                let configurationLost = false;
                try {
                    const { outcome } = await call().pipe(take(1)).toPromise();
                    setState(key, outcomeToState(outcome));
                } catch (error) {
                    // A 400 is the backend's permit.sync.not-configured: the
                    // organisation lost its connector between the page load and this
                    // click. Say so on the step, and stop — the next call would 400
                    // for the same reason.
                    configurationLost = (error as { status?: number })?.status === 400;
                    setState(key, 'failed', configurationLost ? 'not-configured' : undefined);
                }
                settleAt(bound);
                if (configurationLost) return;
            }

            // reloadAll(), not loadCategories(): the latter keeps the type lists it
            // already has, and the types and their forms are exactly what the sync
            // just replaced. Fires the reload rather than awaiting it — it subscribes
            // internally and returns void, so this step completes on trigger.
            setState('refresh', 'active');
            this.categoriesSection?.reloadAll();
            setState('refresh', 'ok');
            settleAt(100);
        } finally {
            if (creep) clearInterval(creep);
            done$.next(true);
            dialogRef.disableClose = false;
        }
    }

    getCreateButtonTranslationKey(): string {
        if (this.selectedSection === 'categories') {
            return 'permit.config.categories.add';
        }
        if (this.selectedSection === 'statuses') {
            return 'permit.config.statuses.add';
        }
        if (this.selectedSection === 'departments') {
            return 'permit.config.departments.add';
        }
        return 'permit.config.common.add';
    }

    setSection(section: PermitConfigSection): void {
        this.resetFormState();
        this.selectedSection = section;
    }

    onHeaderBack(): void {
        if (this.isEditingType && this.typeEditorComponent) {
            this.typeEditorComponent.requestClose();
        } else {
            this.resetFormState();
        }
    }

    onHeaderCreate(): void {
        if (this.selectedSection === 'categories') {
            this.isCreatingCategory = true;
        } else if (this.selectedSection === 'statuses') {
            if (this.statusesLocked) return;
            this.isCreatingStatus = true;
        } else if (this.selectedSection === 'departments') {
            this.departmentNextOrder = this.departmentsSection ? this.departmentsSection.nextOrder : 0;
            this.isCreatingDepartment = true;
        }
    }

    onCategoryCreate(): void {
        this.isCreatingCategory = true;
    }

    onCategoryEdit(category: PermitCategory): void {
        this.selectedCategory = category;
        this.isEditingCategory = true;
    }

    onCategorySaved(): void {
        this.resetFormState();
        if (this.categoriesSection) {
            this.categoriesSection.loadCategories();
        }
    }

    onEditType(event: { category: PermitCategory; typeId: string }): void {
        this.isEditingType = true;
        this.typeEditorCategoryId = event.category._id;
        this.typeEditorTypeId = event.typeId;
    }

    onAddType(category: PermitCategory): void {
        this.isEditingType = true;
        this.typeEditorCategoryId = category._id;
        this.typeEditorTypeId = null;
    }

    onTypeSaved(): void {
        this.resetFormState();
        if (this.categoriesSection) {
            this.categoriesSection.loadCategories();
        }
    }

    onTypeCancel(): void {
        this.resetFormState();
    }

    onStatusEdit(status: PermitStatus): void {
        if (this.statusesLocked) return;
        this.selectedStatus = status;
        this.isEditingStatus = true;
    }

    onStatusSaved(): void {
        this.resetFormState();
        if (this.statusesSection) {
            this.statusesSection.loadStatuses();
        }
    }

    onDepartmentEdit(department: PermitDepartment): void {
        this.selectedDepartment = department;
        this.isEditingDepartment = true;
    }

    onDepartmentSaved(): void {
        this.resetFormState();
        if (this.departmentsSection) {
            this.departmentsSection.loadDepartments();
        }
    }

    private resetFormState(): void {
        this.isCreatingCategory = false;
        this.isEditingCategory = false;
        this.selectedCategory = null;
        this.isCreatingStatus = false;
        this.isEditingStatus = false;
        this.selectedStatus = null;
        this.isCreatingDepartment = false;
        this.isEditingDepartment = false;
        this.selectedDepartment = null;
        this.isEditingType = false;
        this.typeEditorCategoryId = null;
        this.typeEditorTypeId = null;
    }
}
